import React, { PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Program } from 'types/Program';
import { programsApi } from 'api';

type ProgramPanelContextValue = {
	organizationName?: string;
	programName?: string;
	programId?: number;
	data?: Program;
	isLoading?: boolean;
	isSuccess?: boolean;
	isError?: boolean;
	refetch?: () => Promise<any>;
	panelPath?: string;
	isStale?: boolean;
	programPath?: string;
	fullProgramUrl?: string;
	context: 'coordinator' | 'program-admin';
};

export const ProgramPanelContext = createContext<ProgramPanelContextValue>({} as any);

// eslint-disable-next-line @typescript-eslint/ban-types
type ProgramPanelContextProviderProps = PropsWithChildren<{
	context: 'coordinator' | 'program-admin';
}>;

const ProgramPanelContextProvider = ({ context, children }: ProgramPanelContextProviderProps) => {
	const { organizationName, programName } = useParams();

	const getProgramQuery = useMemo(() => {
		switch (context) {
			case 'program-admin':
				return () =>
					programsApi.getProgramAdmin({
						organizationName: String(organizationName),
						programName: String(programName),
					});
			case 'coordinator':
				return () =>
					programsApi.getProgramAsCoordinator({
						organizationName: String(organizationName),
						programName: String(programName),
					});
			default:
				return null;
		}
	}, [context, organizationName, programName]);
	const { data, isSuccess, isLoading, isError, refetch, isStale } = useQuery(
		['currentProgramPanel', { organizationName, programName }],
		getProgramQuery!,
		{ enabled: Boolean(organizationName) && Boolean(programName), staleTime: 10000 }
	);

	const programPath = `${organizationName}/${programName}`;
	const panelPath = useMemo(() => {
		switch (context) {
			case 'program-admin':
				return `program/${programPath}`;
			case 'coordinator':
				return `coordinator/${programPath}`;
			default:
				return '';
		}
	}, [context, programPath]);
	const fullProgramUrl = `${[process.env.REACT_APP_FRONT_URL]}/${programPath}`;

	return (
		<ProgramPanelContext.Provider
			value={{
				organizationName,
				programName,
				programId: data?.id,
				data,
				isSuccess,
				isLoading,
				isError,
				refetch,
				panelPath,
				isStale,
				programPath,
				fullProgramUrl,
				context,
			}}
		>
			{children}
		</ProgramPanelContext.Provider>
	);
};

export const useProgramPanel = () => useContext(ProgramPanelContext);

export default ProgramPanelContextProvider;
